/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { getSecurePostureTag, secureApi, useGetCustomerSecurityPosture } from './secure'
import { buildTemplateChecksFromPosture } from './buildTemplateChecksFromPosture'
import prepareHeaders from '../utils/prepareHeaders'
import { getSelectedCustomerId } from '../selectors/customers'

const reducerPath = 'assessments'
const base = '/secure/assessment'
const url = `${base}/*`

export const assessmentsApi = createApi({
  reducerPath,
  tagTypes: [reducerPath],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTemplates: builder.query({
      providesTags(_, __, id) {
        return [{ type: reducerPath, id }]
      },
      query: (customerId = '*') => `${base}/${customerId}`,
    }),
    getAssessmentCompliance: builder.query({
      providesTags(_, __, customerId) {
        return [{ type: reducerPath, id: `compliance-${customerId}` }]
      },
      query: (customerId = '*') => `${base}/${customerId}/compliance`,
    }),
    getAssessmentPosture: builder.query({
      providesTags(_, __, customerId) {
        return [{ type: reducerPath, id: `posture-${customerId}` }]
      },
      query: (customerId = '*') => `${base}/${customerId}/posture`,
    }),
    syncAssessmentCompliance: builder.mutation({
      invalidatesTags(_, __, customerId) {
        return [
          { type: reducerPath, id: 'posture-*' },
          { type: reducerPath, id: 'compliance-*' },
          { type: reducerPath, id: `compliance-${customerId}` },
        ]
      },
      async onQueryStarted(customerId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // RTK Query cannot invalidate tags across API slices via invalidatesTags.
          dispatch(secureApi.util.invalidateTags([getSecurePostureTag(customerId)]))
        } catch {
          // Leave secure cache unchanged when sync fails.
        }
      },
      query: (customerId) => ({
        url: `${base}/${customerId}/compliance`,
        method: 'PATCH',
      }),
    }),
    createTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: (body) => ({
        body,
        url,
        method: 'POST',
      }),
    }),
    updateTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: ({ id, ...body }) => ({
        body,
        url: `${url}/${id}`,
        method: 'PUT',
      }),
    }),
    updateAssignments: builder.mutation({
      invalidatesTags: [reducerPath],
      query: ({ id, ...body }) => ({
        body,
        url: `${url}/${id}/assignments`,
        method: 'PUT',
      }),
    }),
    deleteTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: (id) => ({
        url: `${url}/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useGetTemplatesQuery,
  useGetAssessmentComplianceQuery,
  useGetAssessmentPostureQuery,
  useSyncAssessmentComplianceMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useUpdateAssignmentsMutation,
  useUpdateTemplateMutation,
} = assessmentsApi

export const useGetAllTemplates = () => useGetTemplatesQuery('*')

export const useGetAllAssessmentPosture = () => useGetAssessmentPostureQuery('*')

export const useGetAllAssessmentCompliance = () => useGetAssessmentComplianceQuery('*')

export const useGetCustomerTemplates = () => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetTemplatesQuery(customerId)
}

export const useGetCustomerTemplate = (templateId) => {
  const { data = [], ...rest } = useGetCustomerTemplates()

  return {
    data: data.find(({ id }) => id === parseInt(templateId, 10)),
    ...rest,
  }
}

export const useGetCustomerTemplateCompliance = () => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetAssessmentComplianceQuery(customerId)
}

export const useGetCustomerTemplateChecks = (templateId) => {
  const { data: securityPosture, isLoading: isSecurityPostureLoading } = useGetCustomerSecurityPosture()
  const { data: templates = [], isLoading: isTemplatesLoading } = useGetCustomerTemplates()
  const isLoading = isSecurityPostureLoading || isTemplatesLoading

  return useMemo(() => {
    if (!securityPosture || !templateId || isLoading) {
      return { isLoading }
    }

    const { securityChecks: { allMonitored = [], ignored = [] } = {} } = securityPosture
    const { checks = [] } = templates.find(({ id }) => id === templateId) ?? {}
    const postureChecks = [...allMonitored, ...ignored]

    return {
      data: buildTemplateChecksFromPosture(checks, postureChecks),
    }
  }, [securityPosture, templateId, templates, isLoading])
}
